import React, { useState, useRef, useEffect, useMemo } from 'react';
import { User } from '../types/user';
import { getSidebarRoutes, isRouteActive } from '../config/routeConfig';

interface SidebarProps {
  currentPath: string;
  onNavigate: (routeId: string, params?: Record<string, string>, data?: Record<string, any>) => void;
  onLogout: () => void;
  user?: User | null;
}

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy:      '#14267A',
  navyDeep:  '#0B1650',
  gold:      '#F5B326',
  rail:      '#FFFFFF', // sidebar panel background (white)
  tint:      '#E8EBF8', // active item background
  railLine:  '#D9DDF0',
  text:      '#2F3558',
  textMuted: '#5A6080',
  hover:     'rgba(20, 38, 122, 0.07)',
};

const Sidebar: React.FC<SidebarProps> = ({ currentPath, onNavigate, onLogout, user }) => {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set(['schoolSetup']));
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [submenuHeights, setSubmenuHeights] = useState<Record<string, number>>({});
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const submenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Get all sidebar routes dynamically - memoize to prevent re-calculation
  const sidebarRoutes = useMemo(() => getSidebarRoutes(), []);

  // Calculate submenu heights
  useEffect(() => {
    const heights: Record<string, number> = {};
    Object.entries(submenuRefs.current).forEach(([id, ref]) => {
      if (ref) {
        heights[id] = ref.scrollHeight;
      }
    });
    setSubmenuHeights(heights);
  }, []); // Only calculate once on mount

  // Auto-expand parent if child is active
  useEffect(() => {
    const newExpanded = new Set<string>();

    sidebarRoutes.forEach(({ id, children }) => {
      if (children && isRouteActive(id, currentPath)) {
        newExpanded.add(id);
      }
    });

    // Only update if there's a change
    const currentExpandedArray = Array.from(expandedItems).sort();
    const newExpandedArray = Array.from(newExpanded).sort();
    const hasChanges = currentExpandedArray.length !== newExpandedArray.length ||
      currentExpandedArray.some((item, index) => item !== newExpandedArray[index]);

    if (hasChanges) {
      setExpandedItems(newExpanded);
    }
  }, [currentPath]); // Only depend on currentPath

  // Collapse all submenus when sidebar is collapsed
  useEffect(() => {
    if (isCollapsed) {
      setExpandedItems(new Set());
    }
  }, [isCollapsed]);

  const toggleExpanded = (routeId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(routeId)) {
      newExpanded.delete(routeId);
    } else {
      newExpanded.add(routeId);
    }
    setExpandedItems(newExpanded);
  };

  const handleMouseEnter = (label: string, event: React.MouseEvent<HTMLButtonElement>) => {
    if (isCollapsed) {
      const rect = event.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top + rect.height / 2,
        left: rect.right + 10
      });
      setHoveredItem(label);
    }
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const handleNavigate = (routeId: string) => {
    onNavigate(routeId, {}, {});
  };

  const displayName = user?.name?.trim() || user?.email?.split('@')[0] || 'Super Admin';
  const roleLabel = (user?.role || 'user').replace(/_/g, ' ');
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <>
      <div style={{
        width: isCollapsed ? '79px' : '245px',
        background: C.rail,
        borderRight: '1px solid #E2E5F2',
        padding: isCollapsed ? '12px 11px 14px' : '12px 14px 14px',
        color: C.text,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.3s ease',
        position: 'relative',
        zIndex: 200, // above the top bar (z-index 100) and content so the toggle is never hidden
        boxSizing: 'border-box',
        fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif"
      }}>
        {/* Logo card with collapse button */}
        <div style={{
          height: isCollapsed ? 'auto' : '48px',
          flexShrink: 0,
          marginBottom: '20px',
          boxSizing: 'border-box',
          border: '1px solid #E2E5F2',
          borderRadius: '14px',
          display: 'flex',
          alignItems: 'center',
          flexDirection: isCollapsed ? 'column' : 'row',
          gap: isCollapsed ? '8px' : '0',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          padding: isCollapsed ? '9px 0' : '0 8px 0 12px',
          boxShadow: '0 2px 8px rgba(20,38,122,0.05)',
        }}>
          {isCollapsed ? (
            // Collapsed: show only the emblem (left part of the logo)
            <div
              role="img"
              aria-label="Hindustan Computer Institute"
              style={{ width: '38px', height: '35px', backgroundImage: 'url(/hci-logo.png)', backgroundRepeat: 'no-repeat', backgroundSize: '89px auto', backgroundPosition: 'left center' }}
            />
          ) : (
            <img
              src="/hci-logo.png"
              alt="Hindustan Computer Institute"
              style={{ height: '34px', width: 'auto', maxWidth: '150px', objectFit: 'contain', display: 'block' }}
            />
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '9px',
              background: C.tint,
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0,
              transition: 'background 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#D9DDF0'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = C.tint; }}
          >
            <i className={`pi ${isCollapsed ? 'pi-angle-double-right' : 'pi-angle-double-left'}`} style={{ fontSize: '0.8rem', color: C.navy }}></i>
          </button>
        </div>

        {!isCollapsed && (
          <div style={{ padding: '0 13px 7px', fontSize: '0.585rem', fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', color: C.textMuted }}>
            Menu
          </div>
        )}

        <nav className="hci-sidebar-nav" style={{
          flex: 1,
          overflowY: 'auto',
          paddingRight: isCollapsed ? '0' : '4px',
        }}>
          {sidebarRoutes.map(({ id, route, children }) => {
            const isActive = isRouteActive(id, currentPath);
            const isExpanded = expandedItems.has(id);
            const hasChildren = children && children.length > 0;
            const isHighlighted = isActive && !hasChildren;

            return (
              <div key={id} style={{ marginBottom: '4px' }}>
                {/* Parent Menu Item */}
                <button
                  onClick={() => {
                    if (hasChildren && !isCollapsed) {
                      toggleExpanded(id);
                    } else if (hasChildren && isCollapsed) {
                      // When collapsed, expand sidebar and show children
                      setIsCollapsed(false);
                      setExpandedItems(new Set([id]));
                    } else {
                      handleNavigate(id);
                    }
                  }}
                  onMouseEnter={(e) => {
                    handleMouseEnter(route.label, e);
                    if (!isHighlighted) {
                      e.currentTarget.style.background = C.hover;
                    }
                  }}
                  onMouseLeave={(e) => {
                    handleMouseLeave();
                    if (!isHighlighted) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '40px',
                    padding: isCollapsed ? '0' : '0 13px',
                    background: isHighlighted ? C.navy : 'transparent',
                    boxShadow: isHighlighted ? '0 6px 14px rgba(20,38,122,0.25)' : 'none',
                    border: 'none',
                    borderRadius: '11px',
                    color: isHighlighted ? 'white' : C.text,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                    fontSize: '0.783rem',
                    fontWeight: isHighlighted ? 700 : 600,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : (hasChildren ? 'space-between' : 'flex-start'),
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <i className={`pi ${route.icon}`} style={{
                      marginRight: isCollapsed ? '0' : '13px',
                      fontSize: '0.99rem'
                    }}></i>
                    {!isCollapsed && route.label}
                  </div>
                  {hasChildren && !isCollapsed && (
                    <i
                      className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}
                      style={{
                        fontSize: '0.72rem',
                        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'
                      }}
                    ></i>
                  )}
                </button>

                {/* Children (Submenu) */}
                {hasChildren && !isCollapsed && (
                  <div
                    ref={(el) => { submenuRefs.current[id] = el; }}
                    style={{
                      marginTop: '2px',
                      marginLeft: '28px',
                      borderLeft: '2px solid #C6CDE8',
                      paddingLeft: '13px',
                      maxHeight: isExpanded ? `${submenuHeights[id] || 1000}px` : '0px',
                      opacity: isExpanded ? 1 : 0,
                      overflow: 'hidden',
                      transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  >
                    {children.map(({ id: childId, route: childRoute }) => {
                      const isChildActive = isRouteActive(childId, currentPath);

                      return (
                        <button
                          key={childId}
                          onClick={() => handleNavigate(childId)}
                          style={{
                            width: '100%',
                            height: '34px',
                            padding: '0 11px',
                            marginBottom: '2px',
                            background: isChildActive ? C.navy : 'transparent',
                            border: 'none',
                            borderRadius: '9px',
                            color: isChildActive ? 'white' : C.textMuted,
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'inherit',
                            fontSize: '0.737rem',
                            fontWeight: isChildActive ? 700 : 600,
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                          }}
                          onMouseEnter={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.background = C.hover;
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isChildActive) {
                              e.currentTarget.style.background = 'transparent';
                            }
                          }}
                        >
                          {childRoute.icon && (
                            <i className={`pi ${childRoute.icon}`} style={{ marginRight: '9px', fontSize: '0.81rem' }}></i>
                          )}
                          {childRoute.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User card + logout */}
        <div style={{
          marginTop: 'auto',
          flexShrink: 0,
          border: isCollapsed ? 'none' : '1px solid #E2E5F2',
          borderRadius: '14px',
          padding: isCollapsed ? '0' : '8px',
          display: 'flex',
          flexDirection: isCollapsed ? 'column' : 'row',
          alignItems: 'center',
          gap: '9px',
          boxShadow: isCollapsed ? 'none' : '0 2px 8px rgba(20,38,122,0.05)',
        }}>
          <div style={{
            width: '34px', height: '34px', borderRadius: '11px', background: C.navy, color: C.gold,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.855rem', fontWeight: 800, flexShrink: 0,
          }}>
            {initial}
          </div>
          {!isCollapsed && (
            <div style={{ flex: 1, minWidth: 0, lineHeight: 1.2 }}>
              <div style={{ color: C.text, fontSize: '0.738rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
              <div style={{ color: C.textMuted, fontSize: '0.63rem', fontWeight: 500, textTransform: 'capitalize' }}>{roleLabel}</div>
            </div>
          )}
          <button
            onClick={onLogout}
            aria-label="Logout"
            onMouseEnter={(e) => { handleMouseEnter('Logout', e); e.currentTarget.style.filter = 'brightness(1.15)'; }}
            onMouseLeave={(e) => { handleMouseLeave(); e.currentTarget.style.filter = 'none'; }}
            style={{
              width: '34px',
              height: '34px',
              flexShrink: 0,
              background: 'radial-gradient(circle at 88% -60%, rgba(245,179,38,0.45) 0%, rgba(245,179,38,0) 45%), radial-gradient(circle at 12% 160%, rgba(96,128,255,0.45) 0%, rgba(96,128,255,0) 50%), linear-gradient(110deg, #0B1650 0%, #14267A 48%, #2A44B8 100%)',
              border: 'none',
              borderRadius: '11px',
              color: 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'filter 0.2s ease',
            }}
          >
            <i className="pi pi-sign-out" style={{ fontSize: '0.85rem' }}></i>
          </button>
        </div>
      </div>

      {/* Tooltip Overlay */}
      {isCollapsed && hoveredItem && (
        <div
          style={{
            position: 'fixed',
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
            transform: 'translateY(-50%)',
            background: C.navy,
            color: 'white',
            padding: '6px 13px',
            borderRadius: '9px',
            fontSize: '0.72rem',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(20, 38, 122, 0.3)',
            zIndex: 9999,
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
            animation: 'tooltipFadeIn 0.2s ease',
          }}
        >
          {hoveredItem}
          <div
            style={{
              position: 'absolute',
              left: '-5px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderRight: `6px solid ${C.navy}`
            }}
          />
        </div>
      )}

      <style>{`
        @keyframes tooltipFadeIn {
          from {
            opacity: 0;
            transform: translateY(-50%) translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(-50%) translateX(0);
          }
        }

        .hci-sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .hci-sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .hci-sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(20, 38, 122, 0.2);
          border-radius: 10px;
        }

        .hci-sidebar-nav::-webkit-scrollbar-thumb:hover {
          background: rgba(20, 38, 122, 0.35);
        }
      `}</style>
    </>
  );
};

export default Sidebar;